import React, { useState, useCallback, useEffect, useRef, memo } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, IconButton, Divider, Tooltip, TextField, Slider, Typography, ToggleButton, ToggleButtonGroup, Button, Popover, Select, MenuItem, CircularProgress, Menu, ListItemIcon, ListItemText, Autocomplete } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { DEFAULT_FONTS } from '../utils/fonts';
import { t } from '../config';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import OpacityIcon from '@mui/icons-material/Opacity';
import CropIcon from '@mui/icons-material/Crop';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import AlignHorizontalLeftIcon from '@mui/icons-material/AlignHorizontalLeft';
import AlignHorizontalCenterIcon from '@mui/icons-material/AlignHorizontalCenter';
import AlignHorizontalRightIcon from '@mui/icons-material/AlignHorizontalRight';
import AlignVerticalTopIcon from '@mui/icons-material/AlignVerticalTop';
import AlignVerticalCenterIcon from '@mui/icons-material/AlignVerticalCenter';
import AlignVerticalBottomIcon from '@mui/icons-material/AlignVerticalBottom';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';

const _positionMenuItemSx = {
  textTransform: 'none',
  fontSize: 12,
  fontWeight: 400,
  color: '#333',
  justifyContent: 'flex-start',
  px: 1,
  py: 0.6,
  minWidth: 0,
  '&:hover': { backgroundColor: 'rgba(13,131,205,0.08)' },
};

// ── Font preview: lightweight Google Font subsets for dropdown ──
const _fontPreviewLoaded = new Set();
function loadFontPreview(fontFamily) {
  if (_fontPreviewLoaded.has(fontFamily)) return;
  _fontPreviewLoaded.add(fontFamily);
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}&text=${encodeURIComponent(fontFamily)}&display=swap`;
  document.head.appendChild(link);
}

const FontPreviewOption = memo(function FontPreviewOption({ fontFamily, ...liProps }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { loadFontPreview(fontFamily); io.disconnect(); } },
      { rootMargin: '200px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [fontFamily]);
  return (
    <li ref={ref} {...liProps}>
      <span style={{ fontFamily: `'${fontFamily}', sans-serif`, fontSize: 14 }}>{fontFamily}</span>
    </li>
  );
});

// Text-specific toolbar
const TextToolbar = observer(({ element, store }) => {
  const handleFontSizeChange = (e) => {
    const val = parseInt(e.target.value);
    if (!isNaN(val) && val > 0) {
      element.set({ fontSize: val });
      store.history.addUndoState();
    }
  };

  const toggleBold = () => {
    element.set({ fontWeight: element.fontWeight === 'bold' ? 'normal' : 'bold' });
    store.history.addUndoState();
  };

  const toggleItalic = () => {
    element.set({ fontStyle: element.fontStyle === 'italic' ? 'normal' : 'italic' });
    store.history.addUndoState();
  };

  const toggleUnderline = () => {
    element.set({
      textDecoration: element.textDecoration === 'underline' ? '' : 'underline',
    });
    store.history.addUndoState();
  };

  const toggleStrikethrough = () => {
    element.set({
      textDecoration: element.textDecoration === 'line-through' ? '' : 'line-through',
    });
    store.history.addUndoState();
  };

  const handleAlignChange = (_, value) => {
    if (value) {
      element.set({ align: value });
      store.history.addUndoState();
    }
  };

  const handleColorChange = (e) => {
    element.set({ fill: e.target.value });
    store.history.addUndoState();
  };

  return (
    <>
      {/* Font family */}
      <Autocomplete
        size="small"
        value={element.fontFamily}
        onChange={(_, value) => {
          if (value) {
            element.set({ fontFamily: value });
            store.loadFont(value);
            store.history.addUndoState();
          }
        }}
        options={(() => {
          const fonts = [...DEFAULT_FONTS];
          if (element.fontFamily && !fonts.includes(element.fontFamily)) {
            fonts.unshift(element.fontFamily);
          }
          return fonts;
        })()}
        disableClearable
        renderOption={(props, option) => (
          <FontPreviewOption key={option} fontFamily={option} {...props} />
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            sx={{ width: 170, '& .MuiInputBase-input': { fontSize: 12, py: 0.5, fontFamily: `'${element.fontFamily}', sans-serif` } }}
          />
        )}
        ListboxProps={{ style: { maxHeight: 300 } }}
        sx={{ width: 170 }}
      />

      {/* Font size */}
      <TextField
        size="small"
        type="number"
        value={element.fontSize}
        onChange={handleFontSizeChange}
        onFocus={(e) => e.target.select()}
        sx={{ width: 85, '& .MuiInputBase-input': { fontSize: 13, py: 0.5, textAlign: 'center' } }}
        inputProps={{ min: 1, max: 500 }}
      />

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Color */}
      <Tooltip title="Text color">
        <input
          type="color"
          value={element.fill || '#000000'}
          onChange={handleColorChange}
          style={{ width: 28, height: 28, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', padding: 0 }}
        />
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Bold, Italic, Underline, Strikethrough */}
      <Tooltip title="Bold">
        <IconButton size="small" onClick={toggleBold} color={element.fontWeight === 'bold' ? 'primary' : 'default'}>
          <FormatBoldIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Italic">
        <IconButton size="small" onClick={toggleItalic} color={element.fontStyle === 'italic' ? 'primary' : 'default'}>
          <FormatItalicIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Underline">
        <IconButton size="small" onClick={toggleUnderline} color={element.textDecoration === 'underline' ? 'primary' : 'default'}>
          <FormatUnderlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Strikethrough">
        <IconButton size="small" onClick={toggleStrikethrough} color={element.textDecoration === 'line-through' ? 'primary' : 'default'}>
          <StrikethroughSIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Alignment */}
      <ToggleButtonGroup size="small" value={element.align} exclusive onChange={handleAlignChange}>
        <ToggleButton value="left" sx={{ p: 0.5 }}>
          <FormatAlignLeftIcon sx={{ fontSize: 16 }} />
        </ToggleButton>
        <ToggleButton value="center" sx={{ p: 0.5 }}>
          <FormatAlignCenterIcon sx={{ fontSize: 16 }} />
        </ToggleButton>
        <ToggleButton value="right" sx={{ p: 0.5 }}>
          <FormatAlignRightIcon sx={{ fontSize: 16 }} />
        </ToggleButton>
      </ToggleButtonGroup>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <Button
        size="small"
        sx={{
          textTransform: 'none', fontSize: 12, whiteSpace: 'nowrap', px: 1.5, minWidth: 'auto',
          ...(store._toolbarPanel?.type === 'effects' ? { backgroundColor: '#e3f2fd', color: '#0d83cd' } : {}),
        }}
        startIcon={<AutoAwesomeIcon sx={{ fontSize: '16px !important' }} />}
        onClick={() => {
          if (store._toolbarPanel?.type === 'effects') {
            store.closeToolbarPanel();
          } else {
            store.openToolbarPanel('effects');
          }
        }}
      >
        {t('toolbar.effects', 'Effects')}
      </Button>
    </>
  );
});

// Toolbar text button style (matches Polotno)
const toolbarBtnSx = {
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '13px',
  color: '#333',
  minWidth: 'auto',
  px: 1.5,
  py: 0.5,
  borderRadius: '6px',
  '&:hover': { backgroundColor: '#f0f0f0' },
};

// Image-specific toolbar
const ImageToolbar = observer(({ element, store }) => {
  const [flipAnchor, setFlipAnchor] = useState(null);

  const handleFlipX = () => {
    element.set({ flipX: !element.flipX });
    store.history.addUndoState();
    setFlipAnchor(null);
  };

  const handleFlipY = () => {
    element.set({ flipY: !element.flipY });
    store.history.addUndoState();
    setFlipAnchor(null);
  };

  const handleCrop = () => {
    if (element.toggleCropMode) {
      element.toggleCropMode();
    }
  };

  const handleFitToPage = () => {
    element.set({
      x: 0,
      y: 0,
      width: store.width,
      height: store.height,
    });
    store.history.addUndoState();
  };

  const handleEffects = () => {
    if (store._toolbarPanel?.type === 'effects') {
      store.closeToolbarPanel();
    } else {
      store.openToolbarPanel('effects');
    }
  };

  const handleMask = () => {
    if (element.clipSrc) {
      element.set({ clipSrc: '' });
      store.history.addUndoState();
    } else {
      if (store._toolbarPanel?.type === 'mask') {
        store.closeToolbarPanel();
      } else {
        store.openToolbarPanel('mask');
      }
    }
  };

  const hasMask = !!element.clipSrc;

  return (
    <>
      {/* Flip dropdown */}
      <Button
        size="small"
        sx={toolbarBtnSx}
        onClick={(e) => setFlipAnchor(e.currentTarget)}
      >
        {t('toolbar.flip', 'Flip')}
      </Button>
      <Menu
        anchorEl={flipAnchor}
        open={Boolean(flipAnchor)}
        onClose={() => setFlipAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, borderRadius: '8px', minWidth: 200 } } }}
      >
        <MenuItem onClick={handleFlipX} sx={{ py: 1 }}>
          <ListItemIcon><SwapHorizIcon fontSize="small" sx={{ color: '#555' }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: 13 }}>{t('toolbar.flipHorizontally', 'Flip horizontally')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleFlipY} sx={{ py: 1 }}>
          <ListItemIcon><SwapVertIcon fontSize="small" sx={{ color: '#555' }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: 13 }}>{t('toolbar.flipVertically', 'Flip vertically')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Effects */}
      <Button
        size="small"
        sx={{
          ...toolbarBtnSx,
          ...(store._toolbarPanel?.type === 'effects' ? { backgroundColor: '#e3f2fd', color: '#0d83cd' } : {}),
        }}
        startIcon={<AutoAwesomeIcon sx={{ fontSize: '16px !important' }} />}
        onClick={handleEffects}
      >
        {t('toolbar.effects', 'Effects')}
      </Button>

      {/* Fit to page */}
      <Button size="small" sx={toolbarBtnSx} onClick={handleFitToPage}>
        {t('toolbar.fitToBackground', 'Fit to page')}
      </Button>

      {/* Apply mask / Remove mask */}
      <Button
        size="small"
        sx={{
          ...toolbarBtnSx,
          ...(hasMask ? { color: '#d32f2f' } : {}),
          ...(store._toolbarPanel?.type === 'mask' ? { backgroundColor: '#e3f2fd', color: '#0d83cd' } : {}),
        }}
        onClick={handleMask}
      >
        {hasMask ? t('toolbar.removeMask', 'Remove mask') : t('toolbar.clip', 'Apply mask')}
      </Button>

      {/* Crop */}
      {/* <Tooltip title={t('toolbar.crop', 'Crop')}>
        <IconButton size="small" onClick={handleCrop}>
          <CropIcon fontSize="small" />
        </IconButton>
      </Tooltip> */}
    </>
  );
});

// Figure-specific toolbar
const FigureToolbar = observer(({ element, store }) => {
  const handleFillChange = (e) => {
    element.set({ fill: e.target.value });
    store.history.addUndoState();
  };

  const handleStrokeChange = (e) => {
    element.set({ stroke: e.target.value });
    store.history.addUndoState();
  };

  const handleStrokeWidthChange = (e) => {
    const val = parseInt(e.target.value);
    if (!isNaN(val) && val >= 0) {
      element.set({ strokeWidth: val });
      store.history.addUndoState();
    }
  };

  const handleCornerRadiusChange = (e) => {
    const val = parseInt(e.target.value);
    if (!isNaN(val) && val >= 0) {
      element.set({ cornerRadius: val });
      store.history.addUndoState();
    }
  };

  return (
    <>
      <Tooltip title={t('toolbar.fill', 'Fill color')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" sx={{ fontSize: 10 }}>{t('toolbar.fill', 'Fill')}</Typography>
          <input
            type="color"
            value={element.fill || '#000000'}
            onChange={handleFillChange}
            style={{ width: 28, height: 28, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', padding: 0 }}
          />
        </Box>
      </Tooltip>
      <Tooltip title={t('toolbar.textStroke', 'Stroke color')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" sx={{ fontSize: 10 }}>{t('toolbar.textStroke', 'Stroke')}</Typography>
          <input
            type="color"
            value={element.stroke || '#000000'}
            onChange={handleStrokeChange}
            style={{ width: 28, height: 28, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', padding: 0 }}
          />
        </Box>
      </Tooltip>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" sx={{ fontSize: 10 }}>{t('toolbar.border', 'Border')}</Typography>
        <TextField
          size="small"
          type="number"
          value={element.strokeWidth || 0}
          onChange={handleStrokeWidthChange}
          onFocus={(e) => e.target.select()}
          sx={{ width: 50, '& .MuiInputBase-input': { fontSize: 11, py: 0.5, textAlign: 'center' } }}
          inputProps={{ min: 0 }}
        />
      </Box>
      {element.subType === 'rect' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" sx={{ fontSize: 10 }}>{t('toolbar.cornerRadius', 'Radius')}</Typography>
          <TextField
            size="small"
            type="number"
            value={element.cornerRadius || 0}
            onChange={handleCornerRadiusChange}
            onFocus={(e) => e.target.select()}
            sx={{ width: 50, '& .MuiInputBase-input': { fontSize: 11, py: 0.5, textAlign: 'center' } }}
            inputProps={{ min: 0 }}
          />
        </Box>
      )}
    </>
  );
});

// Line-specific toolbar
const LineToolbar = observer(({ element, store }) => {
  const handleColorChange = (e) => {
    element.set({ color: e.target.value });
    store.history.addUndoState();
  };

  return (
    <>
      <Tooltip title={t('toolbar.color', 'Line color')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" sx={{ fontSize: 10 }}>{t('toolbar.color', 'Color')}</Typography>
          <input
            type="color"
            value={element.color || '#000000'}
            onChange={handleColorChange}
            style={{ width: 28, height: 28, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', padding: 0 }}
          />
        </Box>
      </Tooltip>
    </>
  );
});

// Position + alignment popover button
const PositionButton = observer(({ element, store }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const pageW = store.width;
  const pageH = store.height;

  const alignEl = (xVal, yVal) => {
    const attrs = {};
    if (xVal !== null) attrs.x = xVal;
    if (yVal !== null) attrs.y = yVal;
    element.set(attrs);
    store.history.addUndoState();
  };

  const layer = (fn) => { fn(); store.history.addUndoState(); };

  return (
    <>
      <Button
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        startIcon={<OpenWithIcon sx={{ fontSize: '16px !important' }} />}
        sx={{
          textTransform: 'none',
          fontSize: 12,
          whiteSpace: 'nowrap',
          px: 1.5,
          minWidth: 'auto',
          ...(open ? { backgroundColor: '#e3f2fd', color: '#0d83cd' } : {}),
        }}
      >
        {t('toolbar.position', 'Position')}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, p: 2, width: 300, borderRadius: '10px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)' } } }}
      >
        {/* Layer order */}
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#111', display: 'block', mb: 1, fontSize: 12 }}>
          {t('toolbar.layering', 'Layer order')}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, mb: 2 }}>
          <Button size="small" startIcon={<ArrowUpwardIcon />} onClick={() => layer(() => element.moveUp())} sx={_positionMenuItemSx}>
            {t('toolbar.up', 'Up')}
          </Button>
          <Button size="small" startIcon={<ArrowDownwardIcon />} onClick={() => layer(() => element.moveDown())} sx={_positionMenuItemSx}>
            {t('toolbar.down', 'Down')}
          </Button>
          <Button size="small" startIcon={<VerticalAlignTopIcon />} onClick={() => layer(() => element.moveTop())} sx={_positionMenuItemSx}>
            {t('toolbar.toForward', 'To front')}
          </Button>
          <Button size="small" startIcon={<VerticalAlignBottomIcon />} onClick={() => layer(() => element.moveBottom())} sx={_positionMenuItemSx}>
            {t('toolbar.toBottom', 'To back')}
          </Button>
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        {/* 3×3 position grid */}
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#111', display: 'block', mb: 1, fontSize: 12 }}>
          {t('toolbar.position', 'Position')}
        </Typography>
        {(() => {
          const cx = (pageW - element.width) / 2;
          const cy = (pageH - element.height) / 2;
          const rx = pageW - element.width;
          const by = pageH - element.height;
          const positions = [
            { label: 'Top left',      x: 0,  y: 0,  row: 0, col: 0 },
            { label: 'Top center',    x: cx, y: 0,  row: 0, col: 1 },
            { label: 'Top right',     x: rx, y: 0,  row: 0, col: 2 },
            { label: 'Middle left',   x: 0,  y: cy, row: 1, col: 0 },
            { label: 'Center',        x: cx, y: cy, row: 1, col: 1 },
            { label: 'Middle right',  x: rx, y: cy, row: 1, col: 2 },
            { label: 'Bottom left',   x: 0,  y: by, row: 2, col: 0 },
            { label: 'Bottom center', x: cx, y: by, row: 2, col: 1 },
            { label: 'Bottom right',  x: rx, y: by, row: 2, col: 2 },
          ];
          return (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5 }}>
              {positions.map(({ label, x, y }) => (
                <Tooltip key={label} title={label} placement="top">
                  <IconButton
                    size="small"
                    onClick={() => alignEl(x, y)}
                    sx={{
                      borderRadius: '6px',
                      border: '1px solid #e0e0e0',
                      p: 0.75,
                      '&:hover': { backgroundColor: 'rgba(13,131,205,0.08)', borderColor: '#0d83cd' },
                    }}
                  >
                    {/* Mini 3×3 dot grid with the active cell highlighted */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 5px)', gap: '2px' }}>
                      {[0,1,2].map((r) => [0,1,2].map((c) => (
                        <Box
                          key={`${r}-${c}`}
                          sx={{
                            width: 5, height: 5, borderRadius: '1px',
                            backgroundColor: (r === positions.find(p => p.label === label).row && c === positions.find(p => p.label === label).col)
                              ? '#0d83cd'
                              : '#ccc',
                          }}
                        />
                      )))}
                    </Box>
                  </IconButton>
                </Tooltip>
              ))}
            </Box>
          );
        })()}
      </Popover>
    </>
  );
});

// Common controls for all element types
const CommonControls = observer(({ element, store }) => {
  const isLocked = element.locked;

  const handleDuplicate = () => {
    if (element.clone) {
      element.clone({ x: (element.x || 0) + 20, y: (element.y || 0) + 20 });
      store.history.addUndoState();
    }
  };

  const handleDelete = () => {
    store.deleteElements([element.id]);
    store.history.addUndoState();
  };

  const handleToggleLock = () => {
    if (isLocked) {
      element.set({ draggable: true, resizable: true, contentEditable: true, styleEditable: true });
    } else {
      element.set({ draggable: false, resizable: false, contentEditable: false, styleEditable: false });
    }
    store.history.addUndoState();
  };

  const handleOpacityChange = (_, value) => {
    element.set({ opacity: value });
  };

  const handleOpacityCommit = () => {
    store.history.addUndoState();
  };

  return (
    <>
      {/* Opacity */}
      <Tooltip title={t('toolbar.opacity', 'Opacity')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: 100 }}>
          <OpacityIcon sx={{ fontSize: 16, color: '#666' }} />
          <Slider
            size="small"
            value={element.opacity}
            onChange={handleOpacityChange}
            onChangeCommitted={handleOpacityCommit}
            min={0}
            max={1}
            step={0.01}
            sx={{ py: 0 }}
          />
        </Box>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Position + alignment */}
      <PositionButton element={element} store={store} />

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Lock */}
      <Tooltip title={isLocked ? t('toolbar.unlockedDescription', 'Unlock') : t('toolbar.lockedDescription', 'Lock')}>
        <IconButton size="small" onClick={handleToggleLock}>
          {isLocked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
        </IconButton>
      </Tooltip>

      {/* Duplicate */}
      <Tooltip title={t('toolbar.duplicateElements', 'Duplicate')}>
        <IconButton size="small" onClick={handleDuplicate}>
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Delete */}
      <Tooltip title={t('toolbar.removeElements', 'Delete')}>
        <IconButton size="small" onClick={handleDelete}>
          <DeleteOutlineIcon fontSize="small" sx={{ color: '#d32f2f' }} />
        </IconButton>
      </Tooltip>
    </>
  );
});

// Download button with format/quality popover
const DefaultDownloadButton = observer(({ store }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [fileType, setFileType] = useState('png');
  // Default quality so pixelRatio=1 (output = template's own pixel size).
  // qualityToPixelRatio(q) = q * 3, so q = 1/3 → pixelRatio = 1.
  const [quality, setQuality] = useState(1 / 3);
  const [exporting, setExporting] = useState(false);

  const open = Boolean(anchorEl);

  const handleClick = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const qualityToPixelRatio = (q) => Math.max(0.5, q * 3);

  const resWidth = Math.round(store.width * qualityToPixelRatio(quality));
  const resHeight = Math.round(store.height * qualityToPixelRatio(quality));

  const handleDownload = useCallback(async () => {
    setExporting(true);
    try {
      const pixelRatio = qualityToPixelRatio(quality);
      switch (fileType) {
        case 'png':
          await store.saveAsImage({
            fileName: 'design.png',
            mimeType: 'image/png',
            pixelRatio,
          });
          break;
        case 'jpeg':
          await store.saveAsImage({
            fileName: 'design.jpg',
            mimeType: 'image/jpeg',
            quality,
            pixelRatio,
          });
          break;
        case 'pdf':
          await store.saveAsPDF({
            fileName: 'design.pdf',
            pixelRatio,
          });
          break;
        case 'json':
          store.saveAsJSON({ fileName: 'design.json' });
          break;
        default:
          break;
      }
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
      handleClose();
    }
  }, [store, fileType, quality]);

  const fileTypeLabel = {
    png: 'PNG',
    jpeg: 'JPEG',
    pdf: 'PDF',
    json: 'JSON',
  };

  return (
    <>
      <Button
        variant="contained"
        size="small"
        onClick={handleClick}
        endIcon={<ArrowDropDownIcon />}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '13px',
          px: 2,
          py: 0.5,
          borderRadius: '6px',
          backgroundColor: '#3d5afe',
          '&:hover': { backgroundColor: '#304ffe' },
        }}
      >
        {t('toolbar.download', 'Download')}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              p: 2,
              width: 260,
              borderRadius: '10px',
              backgroundColor: '#2b2d31',
              color: '#e0e0e0',
            },
          },
        }}
      >
        {/* File type */}
        <Typography variant="caption" sx={{ color: '#aaa', mb: 0.5, display: 'block' }}>
          {t('toolbar.fileType', 'File type')}
        </Typography>
        <Select
          value={fileType}
          onChange={(e) => setFileType(e.target.value)}
          size="small"
          fullWidth
          sx={{
            mb: 2,
            color: '#fff',
            backgroundColor: '#1e1f22',
            borderRadius: '6px',
            '& .MuiSelect-icon': { color: '#aaa' },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#555',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#777',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#3d5afe',
            },
          }}
        >
          <MenuItem value="png">PNG</MenuItem>
          <MenuItem value="jpeg">JPEG</MenuItem>
          <MenuItem value="pdf">PDF</MenuItem>
          <MenuItem value="json">JSON</MenuItem>
        </Select>

        {/* Quality slider (only for image/pdf) */}
        {fileType !== 'json' && (
          <>
            <Typography variant="caption" sx={{ color: '#aaa', mb: 0.5, display: 'block' }}>
              {t('toolbar.quality', 'Quality')}
            </Typography>
            <Slider
              value={quality}
              onChange={(_, v) => setQuality(v)}
              min={0.1}
              max={1}
              step={0.05}
              size="small"
              sx={{
                color: '#3d5afe',
                mb: 1,
                '& .MuiSlider-thumb': { width: 14, height: 14 },
              }}
            />
            <Typography variant="caption" sx={{ color: '#888', mb: 2, display: 'block' }}>
              {resWidth} x {resHeight} px
            </Typography>
          </>
        )}

        {/* Download action */}
        <Button
          variant="contained"
          fullWidth
          onClick={handleDownload}
          disabled={exporting}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '14px',
            py: 1,
            borderRadius: '8px',
            backgroundColor: '#3d5afe',
            '&:hover': { backgroundColor: '#304ffe' },
          }}
        >
          {exporting ? (
            <CircularProgress size={20} sx={{ color: '#fff' }} />
          ) : (
            `${t('toolbar.download', 'Download')} ${fileTypeLabel[fileType]}`
          )}
        </Button>
      </Popover>
    </>
  );
});

// Import button — loads a JSON template file into the store
const DefaultImportButton = observer(({ store }) => {
  const [importing, setImporting] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleFileChange = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      setImporting(true);
      try {
        const text = await file.text();
        const jsonData = JSON.parse(text);
        store.loadJSON(jsonData);
      } catch (err) {
        console.error('Error loading template JSON:', err);
      } finally {
        setImporting(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [store]
  );

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      <Button
        variant="outlined"
        size="small"
        onClick={() => fileInputRef.current?.click()}
        disabled={importing}
        startIcon={
          importing ? (
            <CircularProgress size={16} />
          ) : (
            <FileUploadIcon sx={{ fontSize: 18 }} />
          )
        }
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '13px',
          px: 2,
          py: 0.5,
          mr: 1,
          borderRadius: '6px',
          borderColor: '#3d5afe',
          color: '#3d5afe',
          '&:hover': {
            borderColor: '#304ffe',
            backgroundColor: 'rgba(61,90,254,0.04)',
          },
        }}
      >
        {t('toolbar.import', 'Import')}
      </Button>
    </>
  );
});

const TYPE_TOOLBARS = {
  text: TextToolbar,
  image: ImageToolbar,
  figure: FigureToolbar,
  line: LineToolbar,
  svg: ImageToolbar, // SVG uses same toolbar as image
};

const rowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  px: 1.5,
  py: 0.75,
  overflowX: 'auto',
  overflowY: 'hidden',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
};

const Toolbar = observer(({ store, components = {} }) => {
  const { ActionControls } = components;
  const ImportButton =
    'ImportButton' in components
      ? components.ImportButton
      : DefaultImportButton;
  const DownloadButton =
    'DownloadButton' in components
      ? components.DownloadButton
      : DefaultDownloadButton;

  const selectedElements = store.selectedElements;
  const element = selectedElements.length === 1 ? selectedElements[0] : null;

  const TypeToolbar = element ? TYPE_TOOLBARS[element.type] : null;

  return (
    <Box
      sx={{
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#ffffff',
        flexShrink: 0,
      }}
    >
      {/* ── Row 1: undo/redo + type-specific tools + right-side actions ── */}
      <Box sx={{ ...rowSx, minHeight: 52, borderBottom: '1px solid #f0f0f0' }}>
        {/* Undo / Redo */}
        <Tooltip title={t('toolbar.undo', 'Undo')}>
          <span>
            <IconButton
              size="small"
              onClick={() => store.history.undo()}
              disabled={!store.history.canUndo}
            >
              <UndoIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('toolbar.redo', 'Redo')}>
          <span>
            <IconButton
              size="small"
              onClick={() => store.history.redo()}
              disabled={!store.history.canRedo}
            >
              <RedoIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Multi-select: Group + Delete */}
        {selectedElements.length > 1 && (
          <>
            <Button
              size="small"
              onClick={() => {
                const ids = selectedElements.map((el) => el.id);
                const group = store.groupElements(ids);
                if (group) {
                  store.selectElements([group.id]);
                  store.history.addUndoState();
                }
              }}
              sx={{
                textTransform: 'none',
                fontSize: 13,
                fontWeight: 500,
                px: 1.5,
                py: 0.3,
                minWidth: 0,
                color: '#333',
                '&:hover': { backgroundColor: 'rgba(13,131,205,0.08)' },
              }}
            >
              {t('toolbar.group', 'Group')}
            </Button>
            <Tooltip title={t('toolbar.removeElements', 'Delete')}>
              <IconButton
                size="small"
                onClick={() => {
                  store.deleteElements(selectedElements.map((el) => el.id));
                  store.history.addUndoState();
                }}
              >
                <DeleteOutlineIcon fontSize="small" sx={{ color: '#d32f2f' }} />
              </IconButton>
            </Tooltip>
          </>
        )}

        {/* Single group selected: Ungroup */}
        {element && element.type === 'group' && (
          <Button
            size="small"
            onClick={() => {
              store.ungroupElements([element.id]);
              store.history.addUndoState();
            }}
            sx={{
              textTransform: 'none',
              fontSize: 13,
              fontWeight: 500,
              px: 1.5,
              py: 0.3,
              minWidth: 0,
              color: '#333',
              '&:hover': { backgroundColor: 'rgba(13,131,205,0.08)' },
            }}
          >
            {t('toolbar.ungroup', 'Ungroup')}
          </Button>
        )}

        {/* Type-specific toolbar */}
        {element && TypeToolbar && (
          <TypeToolbar element={element} store={store} />
        )}

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Custom action controls */}
        {ActionControls && <ActionControls store={store} />}

        {/* Import button */}
        {ImportButton && <ImportButton store={store} />}

        {/* Download button */}
        {DownloadButton && <DownloadButton store={store} />}
      </Box>

      {/* ── Row 2: always rendered to reserve height and prevent layout shift ── */}
      <Box sx={{ ...rowSx, minHeight: 44, backgroundColor: '#fafafa', visibility: element ? 'visible' : 'hidden' }}>
        {element && <CommonControls element={element} store={store} />}
      </Box>
    </Box>
  );
});

Toolbar.displayName = 'Toolbar';
export default Toolbar;
export { Toolbar };
